import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, CATEGORY_META, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { DollarSign, MapPin, Clock, Tag, ArrowRight } from "lucide-react";

export default function PostTask() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("errands");
  const [price, setPrice] = useState(15);
  const [location, setLocation] = useState("");
  const [timeWindow, setTimeWindow] = useState("Today flexible");
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      const { data } = await api.post("/tasks", {
        title,
        description,
        category,
        price: Number(price),
        location_text: location,
        time_window: timeWindow,
        city: user?.city,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        // Redirect poster to Stripe Checkout to fund the escrow
        window.location.href = data.checkout_url;
      } else {
        navigate(`/task/${data.task.id}`);
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="post-task-page">
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Post a task.</h1>
      <p className="text-[var(--cs-ink-2)] mt-1">5–30 min. $5–$30. Someone nearby will claim it fast.</p>

      <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
        <label className="block md:col-span-2">
          <span className="text-xs uppercase tracking-widest font-bold">Title</span>
          <input
            required
            data-testid="task-title-input"
            className="brut-input mt-1.5"
            placeholder="Help me carry boxes upstairs"
            maxLength={120}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>

        <label className="block md:col-span-2">
          <span className="text-xs uppercase tracking-widest font-bold">Description</span>
          <textarea
            required
            data-testid="task-description-input"
            className="brut-input mt-1.5 min-h-[110px]"
            placeholder="What needs to happen? Any special instructions?"
            maxLength={1000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <label className="block">
          <span className="text-xs uppercase tracking-widest font-bold">Category</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {Object.entries(CATEGORY_META).map(([key, meta]) => (
              <button
                type="button"
                key={key}
                onClick={() => setCategory(key)}
                data-testid={`cat-pick-${key}`}
                className={`brut-chip ${category === key ? meta.className : "bg-white"}`}
              >
                {meta.label}
              </button>
            ))}
          </div>
        </label>

        <label className="block">
          <span className="text-xs uppercase tracking-widest font-bold">Price ($)</span>
          <div className="mt-1.5 relative">
            <DollarSign size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--cs-money-2)]" />
            <input
              type="number"
              required
              min={1}
              max={500}
              step={1}
              data-testid="task-price-input"
              className="brut-input pl-10 font-display tabular text-2xl font-extrabold"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </div>
        </label>

        <label className="block">
          <span className="text-xs uppercase tracking-widest font-bold">Location</span>
          <div className="mt-1.5 relative">
            <MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--cs-ink-2)]" />
            <input
              required
              data-testid="task-location-input"
              className="brut-input pl-10"
              placeholder="Bedford Ave, Brooklyn"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>
        </label>

        <label className="block">
          <span className="text-xs uppercase tracking-widest font-bold">Time window</span>
          <div className="mt-1.5 relative">
            <Clock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--cs-ink-2)]" />
            <input
              required
              data-testid="task-time-input"
              className="brut-input pl-10"
              placeholder="Today 4–6 PM"
              value={timeWindow}
              onChange={(e) => setTimeWindow(e.target.value)}
            />
          </div>
        </label>

        {err && (
          <div data-testid="post-error" className="md:col-span-2 brut-card p-3 bg-red-50 text-red-700 text-sm font-semibold" style={{ boxShadow: "3px 3px 0 0 #b91c1c", borderColor: "#b91c1c" }}>
            {err}
          </div>
        )}

        <div className="md:col-span-2 flex items-center justify-between gap-4 flex-wrap">
          <div className="text-sm text-[var(--cs-ink-2)]">
            Pay <strong>${Number(price || 0).toFixed(2)}</strong> via Stripe to fund escrow. Released to tasker on completion (minus 12% fee).
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="task-submit-btn"
            className="brut-btn brut-btn-money text-base px-6 py-4"
          >
            {loading ? "Opening Stripe…" : "Continue to payment"} <ArrowRight size={18} />
          </button>
        </div>
      </form>
    </div>
  );
}
